import React, { useContext, useEffect, useState } from 'react';
import { ArrowDownIcon } from '@components/Widgets/layout.components';
import {
  ConditionType,
  DateCondition,
  Expression,
  FeatureFlags,
  Filter,
  formatDate,
  getRootOperator,
  operatorColors,
  operatorLabels,
} from '@fabric-ui/components';
import { Edit, History, Plus, SortDown } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Button, capitalize, Collapse, Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import TopKDisplay from '../../../components/filters/components/TopKDisplay';
import { useAvContext } from '../../../context/AvContextProvider';
import { FINDING_STATE_LABELS } from '../../../utils/filterValueLabels';
import { isNullOrUndefined } from '../../../utils/utils';
import { typeLabelMap } from '../../Sources/Mapping/types';
import { CustomDashboardContext } from '../CustomDashboardContext';
import { useGetDisplayName } from '../hooks/useGetDisplayName';
import { SortBy, TableSubType, TopResults } from '../types';
import { TabsType } from '../types/types';
import { formatTimeRange } from './TimeFilter/utils';

const plusIcon = <Plus style={iconSize(16)} />;
const editIcon = <Edit style={iconSize(16)} />;
const ArrowIcon = isUp => <SortDown style={{ ...iconSize(16), transform: `rotate(${isUp ? -180 : 0}deg)` }} />;

const textStyle = size => ({ fontSize: size === 'small' ? 12 : undefined });

const HEIGHT = 42;
type FiltersDescriptionProps = {
  isFocus: boolean;
};
const FiltersDescription: React.FC<FiltersDescriptionProps> = ({ isFocus }) => {
  const { newWidget, filters, setFilters } = useContext(CustomDashboardContext);
  const {
    requests: [request],
  } = newWidget;
  const { filter, top: requestTop, sorting, timeRange } = request;
  const [isCollapse, setIsCollapse] = useState(true);

  const top =
    newWidget.type === TableSubType.Table
      ? { ...requestTop, groupOthers: !!requestTop?.groupOthers, size: newWidget.definition.custom.topN }
      : requestTop;

  useEffect(() => {
    if (!isCollapse) {
      setIsCollapse(isFocus);
    }
  }, [isFocus]);

  const openFiltersTab = () => setFilters({ ...filters, tab: TabsType.Filters });

  return (
    <Box
      sx={{
        ...flex.col,
        backgroundColor: isFocus ? 'var(--color-surface-interactive-modifier-default)' : 'transparent',
        borderRadius: '12px',
      }}>
      <Box
        onClick={() => setIsCollapse(!isCollapse)}
        sx={{
          ...flex.itemsCenter,
          px: '10px',
          height: HEIGHT,
          gap: 1,
        }}>
        {ArrowDownIcon(isCollapse)}
        <Typography sx={{ fontSize: 11, color: 'var(--color-content-base-primary)' }} variant="h7">
          Filters
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {/* @ts-ignore */}
        <Button onClick={openFiltersTab} sx={{ p: '3px' }} active={isFocus} size="xSmall" variant="outlined" type="icon">
          {filter ? editIcon : plusIcon}
        </Button>
      </Box>
      <Collapse in={isCollapse} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, pb: filter ? 1 : 0 }}>
          <FilterToText filter={filter} top={top} sorting={sorting[0]} projectionId={request.projectionId.name} timeRange={timeRange} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default FiltersDescription;

interface OneLevelTextConditionProps {
  expression: Expression;
  index: number;
  arr: any[];
  operator: string;
  projectionId: string;
  size?: 'small' | 'medium';
  prefix?: string;
  suffix?: string;
  isMainCondition?: boolean;
}

const FIELD_LABEL_MAP: Record<string, Record<string, string>> = {
  'finding.state': FINDING_STATE_LABELS,
};

const getDisplayValue = (value: any, typeCondition: ConditionType, fieldName?: string) => {
  const labelMap = fieldName ? FIELD_LABEL_MAP[fieldName] : undefined;

  if (isNullOrUndefined(value)) {
    return '';
  }

  if (typeCondition === ConditionType.date) {
    if (value?.values) {
      return value?.values.map(v => formatDate(new Date(v), 'MMM dd, yyyy')).join(' - ') || '';
    }

    if (value?.unit) {
      return `${capitalize(value.unit)} ${value.value} ${capitalize(value.periodBehaviour)}`;
    }

    return '';
  }

  if (value?.values) {
    return `[${value?.values.map(v => `"${labelMap?.[v] ?? v}"`).join(', ') ?? ''}]`;
  }

  if (typeof value === 'string') {
    return `"${labelMap?.[value] ?? value}"`;
  }
  if (typeof value === 'boolean') {
    return capitalize(value.toString());
  }
  if (typeof value === 'object') {
    return Object.keys(value).length ? JSON.stringify(value) : '';
  }

  return value.toString();
};

export const OneLevelTextCondition: React.FC<OneLevelTextConditionProps> = ({
  projectionId,
  expression,
  index,
  arr,
  operator,
  size = 'small',
  prefix,
  suffix,
  isMainCondition,
}) => {
  const {
    accountEntities: { fieldTypeMap },
  } = useAvContext();
  const innerExpression = expression?.arrayCondition?.underlying || expression;
  const fieldName = innerExpression?.fieldName;
  const typeCondition =
    ConditionType[typeLabelMap[fieldTypeMap[fieldName || '']]] ||
    Object.values(ConditionType).find(ct => innerExpression?.[ct]) ||
    ConditionType.string;
  const getDisplayName = useGetDisplayName(projectionId);
  const value = innerExpression?.[typeCondition]?.[getRootOperator(innerExpression?.[typeCondition] || {})];

  const displayValue = getDisplayValue(value, typeCondition, fieldName ?? undefined);

  return (
    <Box style={textStyle(size)}>
      {prefix}
      <span style={textStyle(size)}>{getDisplayName(fieldName)}</span>{' '}
      <span style={{ fontWeight: 600, ...textStyle(size) }}>
        {
          operatorLabels[
            `${getRootOperator(innerExpression?.[typeCondition] || {})}${
              expression?.arrayCondition?.resolution ? `|${expression.arrayCondition.resolution}` : ''
            }`
          ]
        }{' '}
      </span>{' '}
      <span style={textStyle(size)}>{displayValue}</span> {suffix}
      {!isMainCondition &&
        (index === arr.length - 1 ? (
          ''
        ) : (
          <Box component="span" sx={{ fontWeight: 600, color: operatorColors[operator], ...textStyle(size) }}>
            {operator.toUpperCase()}{' '}
          </Box>
        ))}
    </Box>
  );
};

type FilterOperandProps = {
  operand: Filter;
  index: number;
  arr: Filter[];
  operator: string;
  projectionId: string;
  size?: 'small' | 'medium';
  prefix?: string;
  suffix?: string;
  isMainCondition?: boolean;
};

const OperatorLine: React.FC<{ operator: string; size?: 'small' | 'medium' }> = ({ operator, size = 'small' }) => (
  <Box style={textStyle(size)}>
    <Box component="span" sx={{ fontWeight: 600, color: operatorColors[operator], ...textStyle(size) }}>
      {operator.toUpperCase()}
    </Box>
  </Box>
);

const FilterOperand: React.FC<FilterOperandProps> = ({
  operand,
  index,
  arr,
  operator,
  projectionId,
  size = 'small',
  prefix,
  suffix,
  isMainCondition,
}) => {
  const isLast = index === arr.length - 1;

  if (operand.expression?.populationCondition) {
    return (
      <>
        <TopKDisplay
          metric={operand.expression.populationCondition.name}
          top={operand.expression.populationCondition.topK.k}
          dir={operand.expression.populationCondition.topK.dir}
          dim={operand.expression.fieldName ?? ''}
        />
        {isMainCondition && !isLast && <OperatorLine operator={operator} size={size} />}
      </>
    );
  }

  if (operand.expression) {
    return (
      <>
        <OneLevelTextCondition
          projectionId={projectionId}
          expression={operand.expression}
          index={index}
          arr={arr}
          operator={operator}
          size={size}
          prefix={prefix}
          suffix={suffix}
          isMainCondition={isMainCondition}
        />
        {isMainCondition && !isLast && <OperatorLine operator={operator} size={size} />}
      </>
    );
  }

  const nestedOperator = getRootOperator(operand);
  const nestedOperands = operand[nestedOperator]?.operands;
  if (!nestedOperands?.length) return null;

  return (
    <>
      <FilterOperands
        operands={nestedOperands}
        operator={nestedOperator}
        projectionId={projectionId}
        size={size}
        prefix={prefix}
        suffix={suffix}
      />
      {!isLast && <OperatorLine operator={operator} size={size} />}
    </>
  );
};

type FilterOperandsProps = {
  operands: Filter[];
  operator: string;
  projectionId: string;
  size?: 'small' | 'medium';
  isMainCondition?: boolean;
  prefix?: string;
  suffix?: string;
};

const FilterOperands: React.FC<FilterOperandsProps> = ({
  operands,
  operator,
  projectionId,
  size = 'small',
  isMainCondition = false,
  prefix,
  suffix,
}) => (
  <>
    {operands.map((operand, index, arr) => {
      const isFirst = index === 0;
      const isLast = index === arr.length - 1;
      return (
        <FilterOperand
          key={index} // eslint-disable-line react/no-array-index-key
          operand={operand}
          index={index}
          arr={arr}
          operator={operator}
          projectionId={projectionId}
          size={size}
          isMainCondition={isMainCondition}
          prefix={isFirst && !isMainCondition ? `${prefix || ''}(` : isFirst ? prefix : undefined}
          suffix={isLast && !isMainCondition ? `)${suffix || ''}` : isLast ? suffix : undefined}
        />
      );
    })}
  </>
);

interface FilterToTextProps {
  filter?: Filter;
  top?: TopResults;
  sorting?: SortBy;
  projectionId: string;
  size?: 'small' | 'medium';
  oneLine?: boolean;
  timeRange?: DateCondition;
}
export const FilterToText: React.FC<FilterToTextProps> = ({ filter, sorting, top, projectionId, size = 'small', oneLine, timeRange }) => {
  const { featureFlags } = useAvContext();
  const getDisplayName = useGetDisplayName(projectionId);
  return (
    <Box sx={{ ...flex.col, gap: 1, ...(oneLine ? { overflow: 'hidden' } : undefined) }}>
      {featureFlags[FeatureFlags.HistoricalAnalytics] && timeRange && (
        <Box sx={{ ...flex.itemsCenter, gap: '6px' }}>
          <History />
          <Box sx={{ fontWeight: 600 }}>Historical</Box>
          <Divider orientation="vertical" sx={{ height: '15px' }} />
          <Box sx={{ fontSize: '12px' }}>{formatTimeRange(timeRange)}</Box>
        </Box>
      )}
      {!isNullOrUndefined(top?.size) && (
        <Box sx={textStyle(size)}>
          Top <b>{top.size}</b>
        </Box>
      )}
      {(sorting || top) && (
        <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
          {sorting && (
            <>
              <Box sx={textStyle(size)}>Sorted By {getDisplayName(sorting.name)}</Box>
              {ArrowIcon(sorting.dir === 'ASC')}
            </>
          )}
        </Box>
      )}
      <Box sx={oneLine ? { whiteSpace: 'pre', overflow: 'hidden', textOverflow: 'ellipsis', '> div': { display: 'inline' } } : undefined}>
        {filter ? (
          <FilterOperands
            operands={filter[getRootOperator(filter)]?.operands ?? []}
            operator={getRootOperator(filter)}
            projectionId={projectionId}
            size={size}
            isMainCondition
          />
        ) : undefined}
      </Box>
    </Box>
  );
};
